import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import authService from '../../services/api/authService';
import { TaskyAlert, TaskyButton, TaskyFormGroup, TaskyInput } from '../tasky';

interface ResetPasswordFormProps {
  token: string;
  email: string;
}

const ResetPasswordForm: React.FC<ResetPasswordFormProps> = ({ token, email }) => {
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [errors, setErrors] = useState<{ password?: string; passwordConfirmation?: string }>({});
  const navigate = useNavigate();

  const getResetPasswordErrorMessage = (error: any): string => {
    const responseData = error?.response?.data;

    if (responseData?.errors) {
      const firstError = Object.values(responseData.errors).reduce<string | undefined>((found, value) => {
        if (found) {
          return found;
        }

        if (Array.isArray(value)) {
          return value.find((item): item is string => typeof item === 'string');
        }

        return typeof value === 'string' ? value : undefined;
      }, undefined);

      if (firstError) {
        return firstError;
      }
    }

    if (responseData?.code === 'passwords.token') {
      return 'パスワード設定リンクが無効、または有効期限が切れています。アカウント設定からログイン情報メールを再送信してください。';
    }

    if (typeof responseData?.message === 'string' && responseData.message.trim()) {
      return responseData.message;
    }

    if (error?.isAxiosError && !error?.response) {
      if (error.code === 'ECONNABORTED') {
        return '通信がタイムアウトしました。時間をおいてから、もう一度お試しください。';
      }

      return '通信に失敗しました。ネットワーク接続を確認してから、もう一度お試しください。';
    }

    if (error instanceof Error && error.message && !error.message.startsWith('Request failed')) {
      return error.message;
    }

    if (!error?.response) {
      return '通信に失敗しました。ネットワーク接続を確認してから、もう一度お試しください。';
    }

    return 'パスワードのリセット中にエラーが発生しました。もう一度お試しください。';
  };

  const validate = (): boolean => {
    const newErrors: { password?: string; passwordConfirmation?: string } = {};
    if (!password) newErrors.password = 'パスワードは必須です';
    else if (password.length < 8) newErrors.password = 'パスワードは8文字以上である必要があります';
    if (!passwordConfirmation) newErrors.passwordConfirmation = 'パスワード確認は必須です';
    else if (password !== passwordConfirmation) newErrors.passwordConfirmation = 'パスワードが一致しません';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    setMessage('');
    try {
      if (!token || !email) {
        throw new Error('パスワード設定リンクが正しくありません。アカウント設定からログイン情報メールを再送信してください。');
      }

      const response = await authService.resetPassword({
        email,
        token,
        password,
        password_confirmation: passwordConfirmation,
      });
      setIsSuccess(true);
      setMessage(response.message || 'パスワードが正常にリセットされました。');
      setTimeout(() => navigate('/login'), 3000);
    } catch (err: any) {
      setIsSuccess(false);
      setMessage(getResetPasswordErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="text-center py-4">
        <div className="flex justify-center mb-4">
          <div className="w-14 h-14 rounded-full bg-success/10 flex items-center justify-center">
            <CheckCircle2 className="w-7 h-7 text-success" />
          </div>
        </div>
        <h1 className="text-xl font-semibold text-dark mb-2">パスワードを変更しました</h1>
        <p className="text-sm text-gray-500 leading-relaxed">{message}</p>
        <p className="text-xs text-gray-400 mt-3">3秒後にログイン画面へ移動します...</p>
      </div>
    );
  }

  return (
    <>
      <div className="text-center mb-6">
        <h1 className="text-xl font-semibold text-dark mb-2">新しいパスワードを設定</h1>
        <p className="text-sm text-gray-500 leading-relaxed">
          新しいパスワードを入力してください。
          <br />
          セキュリティのために、8文字以上で設定してください。
        </p>
      </div>

      {message && !isSuccess && (
        <TaskyAlert tone="error" className="mb-4">
          {message}
        </TaskyAlert>
      )}

      <form onSubmit={handleSubmit} noValidate className="space-y-5">
        <TaskyFormGroup label="新しいパスワード" required htmlFor="password" error={errors.password}>
          <div className="relative">
            <TaskyInput
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setErrors((prev) => ({ ...prev, password: undefined }));
              }}
              placeholder="8文字以上で入力"
              autoComplete="new-password"
              required
              className="pr-10"
              hasError={Boolean(errors.password)}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? 'パスワードを非表示' : 'パスワードを表示'}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </TaskyFormGroup>

        <TaskyFormGroup
          label="パスワード確認"
          required
          htmlFor="passwordConfirm"
          error={errors.passwordConfirmation}
        >
          <div className="relative">
            <TaskyInput
              id="passwordConfirm"
              name="passwordConfirm"
              type={showConfirm ? 'text' : 'password'}
              value={passwordConfirmation}
              onChange={(e) => {
                setPasswordConfirmation(e.target.value);
                setErrors((prev) => ({ ...prev, passwordConfirmation: undefined }));
              }}
              placeholder="パスワードを再入力"
              autoComplete="new-password"
              required
              className="pr-10"
              hasError={Boolean(errors.passwordConfirmation)}
            />
            <button
              type="button"
              onClick={() => setShowConfirm((v) => !v)}
              aria-label={showConfirm ? 'パスワードを非表示' : 'パスワードを表示'}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </TaskyFormGroup>

        <TaskyButton type="submit" isLoading={isSubmitting} className="w-full">
          {isSubmitting ? '処理中...' : 'パスワードを変更'}
        </TaskyButton>
      </form>
    </>
  );
};

export default ResetPasswordForm;
